import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Sparkles, CheckCircle2 } from "lucide-react";

export default function Activate() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [fee, setFee] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login"); return; }
    if (user.subscription_status === "active") {
      navigate(user.role === "organizer" ? "/organizer/dashboard" : "/vendor/dashboard");
      return;
    }
    api.get("/payments/signup/fee").then(({ data }) => setFee(data));
  }, [user, loading, navigate]);

  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/signup/session", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Payment failed to start");
      setBusy(false);
    }
  };

  if (loading || !user || !fee) return <div className="p-16 text-center font-serif text-2xl">Loading…</div>;

  const isVendor = user.role === "vendor";
  const perks = isVendor
    ? ["Unlimited event applications", "Full portfolio with images & bio", "Application status tracking", "Priority listing when new events open"]
    : ["Unlimited event listings", "Vendor application inbox", "Approve/reject with one click", "Collect booth fees via Stripe"];

  return (
    <div className="mx-auto max-w-2xl px-6 py-20">
      <div className="overline" style={{ color: "var(--terracotta)" }}>One more step</div>
      <h1 className="font-serif text-5xl mt-3">Activate your {isVendor ? "maker" : "host"} account.</h1>
      <p className="mt-4 text-base leading-relaxed" style={{ color: "var(--ink-soft)" }}>
        A one-time signup fee keeps the marketplace curated and spam-free.
        You'll unlock everything below the moment your payment goes through.
      </p>

      <div className="card-warm rounded-sm p-10 mt-10">
        <div className="flex items-baseline gap-3">
          <Sparkles className="h-6 w-6" style={{ color: "var(--terracotta)" }} />
          <div className="overline" style={{ color: "var(--ink-soft)" }}>{isVendor ? "Maker plan" : "Host plan"}</div>
        </div>
        <div className="font-serif text-6xl mt-4" data-testid="activate-fee">
          ${fee.amount.toFixed(0)}
          <span className="text-lg font-sans ml-2" style={{ color: "var(--ink-soft)" }}>one-time</span>
        </div>
        <ul className="mt-8 space-y-3">
          {perks.map((p) => (
            <li key={p} className="flex items-start gap-2 text-sm">
              <CheckCircle2 className="h-4 w-4 mt-0.5" style={{ color: "var(--moss)" }} />
              <span>{p}</span>
            </li>
          ))}
        </ul>
        <Button data-testid="activate-pay" onClick={pay} disabled={busy} className="btn-primary w-full py-6 rounded-full mt-10">
          {busy ? "Redirecting to Stripe…" : `Pay $${fee.amount.toFixed(0)} & activate`}
        </Button>
        <div className="text-xs mt-4 text-center" style={{ color: "var(--ink-soft)" }}>
          Payment via Stripe. Cancel anytime — you can log out and come back later.
        </div>
      </div>

      <div className="mt-8 text-center">
        <button data-testid="activate-logout" onClick={async () => { await logout(); navigate("/"); }} className="text-sm underline" style={{ color: "var(--ink-soft)" }}>
          Sign out and return to home
        </button>
      </div>
    </div>
  );
}
